// Maintenance préventive : plans d'entretien par équipement, échéances, historique des interventions.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, MenuItem,
  Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { AddOutlined, DeleteOutline, EditOutlined, TaskAltOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Panel } from '../../components/ui';
import { planningAPI } from '../../services/api';
import { apiError, dateFr, fcfa } from '../../utils/format';
import { ETATS_MAINT, aujourdhui } from './common';

const vide = { equipement: '', operation: '', periodicite_jours: '30', derniere_le: '', prochaine_le: '', immobilisation_id: '', responsable_id: '', instructions: '', actif: true };
const PERIODES = [[7, 'Chaque semaine'], [15, 'Tous les 15 jours'], [30, 'Chaque mois'], [90, 'Chaque trimestre'], [180, 'Chaque semestre'], [365, 'Chaque année']];

function PlanDialog({ open, plan, immobilisations, onClose, onSaved }) {
  const [f, setF] = useState(vide);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open) return;
    setError('');
    setF(plan ? { ...vide, ...Object.fromEntries(Object.keys(vide).map((k) => [k, plan[k] ?? vide[k]])), periodicite_jours: String(plan.periodicite_jours) } : vide);
    planningAPI.utilisateurs().then((r) => setUsers(r.data)).catch((e) => setError(apiError(e)));
  }, [open, plan]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const choisirImmo = (e) => {
    const id = e.target.value; const im = immobilisations.find((i) => i.id === id);
    setF((x) => ({ ...x, immobilisation_id: id, equipement: x.equipement || im?.designation || '' }));
  };
  const save = async () => {
    setError('');
    try {
      const data = { ...f, periodicite_jours: Number(f.periodicite_jours) };
      const r = plan ? await planningAPI.modifierMaintenance(plan.id, data) : await planningAPI.creerMaintenance(data);
      onSaved(r.data.message);
    } catch (e) { setError(apiError(e)); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{plan ? 'Modifier le plan de maintenance' : 'Nouveau plan de maintenance'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          {immobilisations.length > 0 && (
            <Grid item xs={12}>
              <TextField select fullWidth label="Immobilisation liée (facultatif)" value={f.immobilisation_id} onChange={choisirImmo}>
                <MenuItem value=""><em>Aucune</em></MenuItem>
                {immobilisations.map((i) => <MenuItem key={i.id} value={i.id}>{i.designation}</MenuItem>)}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} sm={6}><TextField fullWidth label="Équipement" value={f.equipement} onChange={set('equipement')} placeholder="ex. Monorail 10 t" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Opération" value={f.operation} onChange={set('operation')} placeholder="ex. Graissage, vidange" /></Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Périodicité (jours)" value={f.periodicite_jours} onChange={(e) => setF((x) => ({ ...x, periodicite_jours: e.target.value.replace(/[^\d]/g, '') }))}
              helperText={<Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>{PERIODES.map(([j, l]) => (
                <Chip key={j} size="small" label={l} variant={Number(f.periodicite_jours) === j ? 'filled' : 'outlined'} onClick={() => setF((x) => ({ ...x, periodicite_jours: String(j) }))} />))}</Box>} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Responsable" value={f.responsable_id} onChange={set('responsable_id')} helperText="Reçoit les rappels">
              <MenuItem value=""><em>—</em></MenuItem>
              {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.nom}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}><TextField fullWidth type="date" label="Dernière réalisation" InputLabelProps={{ shrink: true }} inputProps={{ max: aujourdhui() }} value={f.derniere_le} onChange={set('derniere_le')} /></Grid>
          <Grid item xs={6}><TextField fullWidth type="date" label="Prochaine échéance" InputLabelProps={{ shrink: true }} value={f.prochaine_le} onChange={set('prochaine_le')} helperText="Vide : dernière + périodicité" /></Grid>
          <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Instructions" value={f.instructions} onChange={set('instructions')} placeholder="Points de contrôle, pièces, produits…" /></Grid>
          {plan && <Grid item xs={12}><FormControlLabel control={<Switch checked={f.actif} onChange={(e) => setF((x) => ({ ...x, actif: e.target.checked }))} />} label="Plan actif (désactiver pour un équipement à l'arrêt ou vendu)" /></Grid>}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={!f.equipement.trim() || !f.operation.trim() || !f.periodicite_jours} onClick={save}>Enregistrer</Button>
      </DialogActions>
    </Dialog>
  );
}

function RealiserDialog({ plan, onClose, onSaved }) {
  const [f, setF] = useState({ date: aujourdhui(), intervenant: '', cout: '', commentaire: '' });
  const [error, setError] = useState('');
  useEffect(() => { if (plan) { setF({ date: aujourdhui(), intervenant: '', cout: '', commentaire: '' }); setError(''); } }, [plan]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setError('');
    try { const r = await planningAPI.realiser(plan.id, f); onSaved(r.data.message); } catch (e) { setError(apiError(e)); }
  };
  return (
    <Dialog open={!!plan} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Entretien réalisé</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {plan && <Typography sx={{ mb: 2, fontSize: 14 }}><b>{plan.equipement}</b> — {plan.operation}</Typography>}
        <Grid container spacing={2}>
          <Grid item xs={12}><TextField fullWidth type="date" label="Date" InputLabelProps={{ shrink: true }} inputProps={{ max: aujourdhui() }} value={f.date} onChange={set('date')} /></Grid>
          <Grid item xs={12}><TextField fullWidth label="Intervenant" value={f.intervenant} onChange={set('intervenant')} placeholder="Technicien, prestataire…" /></Grid>
          <Grid item xs={12}><TextField fullWidth label="Coût (F)" value={f.cout} onChange={set('cout')} inputProps={{ inputMode: 'numeric' }} /></Grid>
          <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Commentaire" value={f.commentaire} onChange={set('commentaire')} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Annuler</Button><Button variant="contained" color="success" onClick={save}>Enregistrer</Button></DialogActions>
    </Dialog>
  );
}

function HistoriqueDialog({ id, onClose, onEdit, onRealiser }) {
  const [m, setM] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { setM(null); setError(''); if (id) planningAPI.maintenance(id).then((r) => setM(r.data)).catch((e) => setError(apiError(e))); }, [id]);
  return (
    <Dialog open={!!id} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{m ? `${m.equipement} — ${m.operation}` : 'Maintenance'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{error}</Alert>}
        {m && (
          <>
            <Chip size="small" color={ETATS_MAINT[m.etat].color} label={ETATS_MAINT[m.etat].label} sx={{ mb: 1 }} />
            <Typography sx={{ fontSize: 14 }}>Tous les <b>{m.periodicite_jours} jours</b> · prochaine le <b>{dateFr(m.prochaine_le)}</b> · responsable <b>{m.responsable_nom || '—'}</b></Typography>
            {m.immobilisation && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Immobilisation : {m.immobilisation}</Typography>}
            {m.instructions && <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 14, bgcolor: 'action.hover', p: 1.5, borderRadius: 1, my: 1.5 }}>{m.instructions}</Typography>}
            <Typography sx={{ fontWeight: 700, fontSize: 14, mt: 2, mb: 1 }}>Historique</Typography>
            {m.historique.length ? (
              <Table size="small">
                <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Intervenant</TableCell><TableCell align="right">Coût</TableCell><TableCell>Commentaire</TableCell></TableRow></TableHead>
                <TableBody>{m.historique.map((h) => (
                  <TableRow key={h.id}><TableCell>{dateFr(h.date)}</TableCell><TableCell>{h.intervenant || '—'}</TableCell>
                    <TableCell align="right">{h.cout !== null ? `${fcfa(h.cout)} F` : '—'}</TableCell><TableCell sx={{ fontSize: 13 }}>{h.commentaire || ''}<Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>saisi par {h.saisi_par}</Typography></TableCell></TableRow>
                ))}</TableBody>
              </Table>
            ) : <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Aucune intervention enregistrée.</Typography>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {m?.responsable && <Button startIcon={<EditOutlined />} onClick={() => onEdit(m)}>Modifier</Button>}
        {m?.actif && <Button color="success" startIcon={<TaskAltOutlined />} onClick={() => onRealiser(m)}>Entretien réalisé</Button>}
        <Box sx={{ flex: 1 }} /><Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}

const Maintenance = ({ id, onInfo, refreshResume }) => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const [realiser, setRealiser] = useState(null);
  const [error, setError] = useState('');
  const charger = useCallback(() => planningAPI.maintenances().then((r) => setD(r.data)).catch((e) => setError(apiError(e))), []);
  useEffect(() => { charger(); }, [charger]);
  const fait = (m) => { charger(); refreshResume(); if (m) onInfo(m); };
  const supprimer = async (m) => {
    if (!window.confirm(`Supprimer le plan « ${m.equipement} — ${m.operation} » et son historique ?`)) return;
    try { const r = await planningAPI.supprimerMaintenance(m.id); fait(r.data.message); } catch (e) { setError(apiError(e)); }
  };
  const compte = (e) => d?.maintenances.filter((m) => m.etat === e).length || 0;

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Panel flush>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
          {compte('EN_RETARD') > 0 && <Chip color="error" label={`${compte('EN_RETARD')} en retard`} />}
          {compte('A_FAIRE') > 0 && <Chip color="warning" label={`${compte('A_FAIRE')} à faire sous 7 jours`} />}
          {d && !compte('EN_RETARD') && !compte('A_FAIRE') && <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>Aucun entretien en attente.</Typography>}
          <Box sx={{ flex: 1 }} />
          {d?.responsable && <Button variant="contained" startIcon={<AddOutlined />} onClick={() => setEdit({})}>Nouveau plan</Button>}
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow><TableCell>Équipement</TableCell><TableCell>Opération</TableCell><TableCell>Fréquence</TableCell><TableCell>Dernière</TableCell><TableCell>Prochaine</TableCell><TableCell>Responsable</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {d?.maintenances.map((m) => (
                <TableRow key={m.id} hover sx={{ cursor: 'pointer', opacity: m.actif ? 1 : 0.55 }} onClick={() => navigate(`/planification/maintenance/${m.id}`)}>
                  <TableCell><b>{m.equipement}</b></TableCell>
                  <TableCell>{m.operation}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.periodicite_jours} j</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.derniere_le ? dateFr(m.derniere_le) : '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateFr(m.prochaine_le)}<br />
                    <Chip size="small" color={ETATS_MAINT[m.etat].color} label={m.etat === 'EN_RETARD' ? `${-m.jours_restants} j de retard` : m.etat === 'A_FAIRE' ? (m.jours_restants ? `dans ${m.jours_restants} j` : "aujourd'hui") : ETATS_MAINT[m.etat].label} /></TableCell>
                  <TableCell>{m.responsable_nom || '—'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    {m.actif && <Button size="small" color="success" startIcon={<TaskAltOutlined />} onClick={() => setRealiser(m)}>Réalisé</Button>}
                    {d.responsable && <Tooltip title="Supprimer"><IconButton size="small" color="error" aria-label={`Supprimer ${m.equipement}`} onClick={() => supprimer(m)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>}
                  </TableCell>
                </TableRow>
              ))}
              {d && !d.maintenances.length && <TableRow><TableCell colSpan={7} sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>
                {d.responsable ? 'Aucun plan de maintenance. Créez-en un par équipement : monorail, bétonnières, centrale solaire, forage, camions…' : "Aucune maintenance ne vous est confiée."}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
      <PlanDialog open={!!edit} plan={edit?.id ? edit : null} immobilisations={d?.immobilisations || []} onClose={() => setEdit(null)} onSaved={(m) => { setEdit(null); navigate('/planification/maintenance'); fait(m); }} />
      <RealiserDialog plan={realiser} onClose={() => setRealiser(null)} onSaved={(m) => { setRealiser(null); navigate('/planification/maintenance'); fait(m); }} />
      <HistoriqueDialog id={!edit && !realiser ? id : null} onClose={() => navigate('/planification/maintenance')} onEdit={setEdit} onRealiser={setRealiser} />
    </>
  );
};

export default Maintenance;
